/**
 * @file restoreBodyInteraction.ts
 * @brief Clears Radix scroll-lock / pointer-events leftovers on document.body.
 *
 * @details
 * Nested DismissableLayer trees (DropdownMenu → Dialog/AlertDialog) can leave
 * `pointer-events: none` on body after close — sidebar NavLinks stop working.
 * Call after modal/menu close animations (onCloseAutoFocus).
 *
 * @author DES
 * @date 18-06-2026
 */

export function restoreBodyInteraction(): void {
  document.body.style.pointerEvents = "";
  document.body.style.overflow = "";
  document.body.removeAttribute("data-scroll-locked");
}

/** Defer an action until the current Radix dropdown menu has unmounted. */
export function deferAfterDropdownMenu(action: () => void): void {
  window.setTimeout(action, 0);
}

const MENU_UP =
  // `data-vz-popover`: the date popovers (range and single), which are not
  // Radix and so carry no data-state.
  '[role="menu"][data-state], [role="listbox"][data-state], [data-vz-popover]';

/**
 * Presses that began while a menu was up, noted at the very start of the
 * press (capture, on window), before any menu reacts. A Select list leaves
 * the DOM the moment its own outside-press closes it (no exit animation,
 * unlike DropdownMenu), so by the time a sheet asks, it is already gone and
 * the form closed with it (DES 19-10-2026: "click outside the drop menu, it
 * closes the forms").
 */
const pressedWithMenuUp = new WeakSet<Event>();
if (typeof window !== "undefined") {
  window.addEventListener(
    "pointerdown",
    (e) => {
      if (document.querySelector(MENU_UP)) pressedWithMenuUp.add(e);
    },
    true,
  );
}

/**
 * For a Dialog / Sheet's `onPointerDownOutside`: while a menu or select list
 * opened from inside it is still up, a press outside that menu must only
 * close the menu. Radix turns the sheet's content to `pointer-events: none`
 * under a modal menu, so a press anywhere on the form lands on the overlay
 * and closed the whole form (DES 30-09-2026: "open a dropdown and click
 * somewhere else"). Returns true when the press was swallowed.
 */
export function keepOpenWhileMenuUp(event: {
  preventDefault: () => void;
  detail?: { originalEvent?: Event };
}): boolean {
  // The menu still in the DOM (a DropdownMenu through its exit animation), or
  // one that was up when this press began (a Select, already unmounted).
  const original = event.detail?.originalEvent;
  const up =
    document.querySelector(MENU_UP) || (original ? pressedWithMenuUp.has(original) : false);
  if (up) {
    event.preventDefault();
    return true;
  }
  return false;
}
